import React, { useEffect, useMemo, useState } from 'react';
import { getPacientes } from '../../api/pacienteService';
import { getServicios } from '../../api/servicioService';
import { crearCita } from '../../api/citaService';

const modalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.45)',
  zIndex: 1200,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '16px',
};

const modalCardStyle = {
  width: '100%',
  maxWidth: '760px',
  maxHeight: '90vh',
  overflowY: 'auto',
  backgroundColor: 'white',
  borderRadius: '14px',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.28)',
  border: '1px solid #e2e8f0',
  padding: '24px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #dbe2ef',
  borderRadius: '8px',
  fontSize: '14px',
};

const NuevaCitaModal = ({ isOpen, onClose, onCreated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [pacientes, setPacientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [form, setForm] = useState({
    pacienteId: '',
    servicioId: '',
    fecha: '',
    horaInicio: '',
    motivoConsulta: '',
  });

  const pacientesOptions = useMemo(() => {
    return pacientes.map((p) => ({
      id: p.id,
      label: `${p.numero ? `P-${String(p.numero).padStart(4, '0')} - ` : ''}${p.nombre} ${p.apellido} (${p.cedula})`,
    }));
  }, [pacientes]);

  const serviciosOptions = useMemo(() => {
    return servicios.map((s) => ({
      id: s.id,
      label: `${s.nombre} - $${s.precio}`,
    }));
  }, [servicios]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const cargarCatalogos = async () => {
      try {
        setIsLoading(true);
        setError('');

        const [pacientesResponse, serviciosResponse] = await Promise.all([
          getPacientes({ page: 1, limit: 200 }),
          getServicios(),
        ]);

        const pacientesData =
          pacientesResponse?.data?.data?.pacientes ||
          pacientesResponse?.data?.pacientes ||
          pacientesResponse?.data?.data ||
          [];

        const serviciosData =
          serviciosResponse?.data?.data ||
          serviciosResponse?.data?.servicios ||
          [];

        setPacientes(Array.isArray(pacientesData) ? pacientesData : []);
        setServicios(Array.isArray(serviciosData) ? serviciosData : []);
      } catch (catalogError) {
        console.error('Error al cargar catálogos de nueva cita:', catalogError);
        setError('No se pudieron cargar pacientes/servicios para crear la cita.');
      } finally {
        setIsLoading(false);
      }
    };

    cargarCatalogos();
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.pacienteId || !form.servicioId || !form.fecha || !form.horaInicio) {
      setError('Paciente, servicio, fecha y hora son obligatorios.');
      return;
    }

    try {
      setIsSaving(true);
      setError('');

      await crearCita({
        pacienteId: form.pacienteId,
        servicioId: form.servicioId,
        fecha: form.fecha,
        horaInicio: form.horaInicio,
        motivoConsulta: form.motivoConsulta || '',
      });

      setForm({
        pacienteId: '',
        servicioId: '',
        fecha: '',
        horaInicio: '',
        motivoConsulta: '',
      });

      if (onCreated) {
        await onCreated();
      }
    } catch (submitError) {
      console.error('Error al crear cita:', submitError);
      const message =
        submitError?.response?.data?.message ||
        submitError?.message ||
        'No se pudo crear la cita.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={modalBackdropStyle}>
      <div style={modalCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <h3 style={{ margin: 0, color: '#1e293b' }}>Nueva Cita</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', backgroundColor: 'transparent', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
            aria-label="Cerrar"
          >
            x
          </button>
        </div>

        {error ? (
          <div style={{ marginBottom: '16px', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' }}>
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: '#475569' }}>Cargando formulario de cita...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px' }}>Paciente</label>
                <select name="pacienteId" value={form.pacienteId} onChange={handleChange} style={inputStyle}>
                  <option value="">Seleccione...</option>
                  {pacientesOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px' }}>Servicio</label>
                <select name="servicioId" value={form.servicioId} onChange={handleChange} style={inputStyle}>
                  <option value="">Seleccione...</option>
                  {serviciosOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px' }}>Fecha</label>
                <input type="date" name="fecha" value={form.fecha} onChange={handleChange} style={inputStyle} />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px' }}>Hora</label>
                <input type="time" name="horaInicio" value={form.horaInicio} onChange={handleChange} style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px' }}>Motivo de consulta (opcional)</label>
              <textarea
                name="motivoConsulta"
                value={form.motivoConsulta}
                onChange={handleChange}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Describe brevemente el motivo"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #dbe2ef', backgroundColor: 'white', color: '#334155', cursor: 'pointer' }}
              >
                Cerrar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: 'white', cursor: 'pointer', opacity: isSaving ? 0.7 : 1 }}
              >
                {isSaving ? 'Guardando...' : 'Crear Cita'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default NuevaCitaModal;
